import { SubscriptionName } from '@anm/api/modules/subscription';
import PricingSection from '@anm/components/pricing/PricingSection';
import { sliderData } from '@anm/data/pricing/sliderData';
import useFetchVideosManifest from '@anm/hooks/useFetchVideoManifest';
import { useCallback, useEffect, FC } from 'react';
import { useSelector } from 'react-redux';

import useChangePeriodHandler from '../../../hooks/subscription/useChangePeriodHandler';
import useUserCard from '../../../hooks/user/useUserCard';
import routes from '../../../routes';
import { pricingSelectors } from '../../../store/modules/pricing';
import EditCreditCard from '../EditCreditCard';
import SuccessEditCreditCardAlert from '../alerts/SuccessEditCreditCardAlert';
import SuccessUpgradeAlert from '../alerts/SuccessUpgradeAlert';

import useCollapseQuestions from './hooks/useCollapseQuestions';
import useDiscount from './hooks/useDiscount';
import useEditCreditCardModal from './hooks/useEditCreditCardModal';
import useGetPlanHandler from './hooks/useGetPlanHandler';
import usePricingError from './hooks/usePricingError';
import usePricingLoader from './hooks/usePricingLoader';
import useSeasonalDiscount from './hooks/useSeasonalDiscount';
import useUpgradeUserSubscriptionModal from './hooks/useUpgradeUserSubscriptionModal';

export type PricingSubscriptionContentProps = {};

const { Router } = routes;

const PricingSubscriptionContent: FC<PricingSubscriptionContentProps> = () => {
  const videoData = useFetchVideosManifest();
  const plans = useSelector(pricingSelectors.selectPricingSubscriptionPlans);
  const discount = useDiscount();
  const [collapseQuestions] = useCollapseQuestions();
  const handleChangePeriod = useChangePeriodHandler();
  const [isEditCreditCardOpen, handleCloseModal] = useEditCreditCardModal();
  const [isDataLoading, spinner] = usePricingLoader();
  const [isPricingError, pricingError] = usePricingError();
  const seasonalDiscount = useSeasonalDiscount();
  const handleSuccessEditCreditCardAlertShown = useCallback(
    () => Router.pushRoute('pricing', undefined, { shallow: true }),
    []
  );
  const card = useUserCard();

  const [upgradeUserSubscriptionModal, handleUpgradeModalOpen, subscriptionName] = useUpgradeUserSubscriptionModal();

  const handleGetPlan = useGetPlanHandler(handleUpgradeModalOpen);

  useEffect(() => {
    const subscriptionName = Router.query.plan as SubscriptionName;
    subscriptionName && handleUpgradeModalOpen(subscriptionName);
  }, []);

  if (isDataLoading) return spinner;
  if (isPricingError) return pricingError;
  if (!plans) throw new Error('no plans data');

  const cardModalTitle = card ? 'Change Card' : 'Add Card';

  return (
    <>
      <SuccessUpgradeAlert subscriptionName={subscriptionName} />
      <SuccessEditCreditCardAlert action="changed" onAlertShown={handleSuccessEditCreditCardAlertShown} />
      {isEditCreditCardOpen && <EditCreditCard onModalClose={handleCloseModal}>{cardModalTitle}</EditCreditCard>}
      {upgradeUserSubscriptionModal}

      <PricingSection
        {...{ videoData, discount }}
        list={plans}
        sliderData={sliderData}
        seasonalDiscount={seasonalDiscount || undefined}
        collapseQuestions={collapseQuestions}
        onPeriodChange={handleChangePeriod}
        onGetPlanClick={handleGetPlan}
      />
    </>
  );
};

export default PricingSubscriptionContent;
